/**
 * Shared preview, collaboration phase 3 ("see what X sees", presence only, never stored): publishes where this tab's
 * preview is (`{ kind: 'preview', service, route }`, the route read from the preview iframe when it is same-origin,
 * as Flow's path-based preview proxy is) and hands a "see what X sees" request (`collab:openPreview`, from the
 * presence list) to `onOpen({ service, route })`, which opens the person's OWN preview pane there.
 *
 * Phase 4: the pointer over the preview (same-origin only: the iframe's own mouse moves), as fractions of its size,
 * for the others' shared preview thumbnail (components/collab/SharedPreviewThumbnail.jsx); none when it leaves.
 */
import { useEffect, useRef, useState } from 'react';
import { usePresenceView } from './useProjectPresence';
import { setPresencePointer } from '../utils/presenceClient';
import { routeFromUrls } from '../utils/collab3Model';

const POLL_MS = 1500;

export default function useSharedPreview({ projectId, tab, iframeRef, onOpen }) {
  const [route, setRoute] = useState('/');
  const base = tab?.baseUrl || tab?.url || null;

  useEffect(() => {
    setRoute('/');
    if (!projectId || !base || !iframeRef) { return undefined; }
    const read = () => {
      try {
        const href = iframeRef.current?.contentWindow?.location?.href;
        const next = routeFromUrls(href, base);
        if (next) { setRoute((current) => (current === next ? current : next)); }
      } catch {
        // cross-origin preview: its route stays unknown ('/')
      }
    };
    read();
    const timer = setInterval(read, POLL_MS);
    return () => clearInterval(timer);
  }, [projectId, base, iframeRef]);

  usePresenceView(projectId, 'preview', projectId && tab?.url
    ? { kind: 'preview', service: tab.serviceName ? String(tab.serviceName) : undefined, route }
    : null);

  // The pointer over the preview, for the others' thumbnail (same-origin previews only).
  useEffect(() => {
    if (!projectId || !base || !iframeRef) { return undefined; }
    let doc = null;
    const onMove = (event) => {
      const view = doc?.defaultView;
      if (!view || !view.innerWidth || !view.innerHeight) { return; }
      setPresencePointer(projectId, { x: event.clientX / view.innerWidth, y: event.clientY / view.innerHeight });
    };
    const onLeave = () => setPresencePointer(projectId, null);
    const attach = () => {
      try {
        const next = iframeRef.current?.contentDocument || null;
        if (next === doc) { return; }
        if (doc) { doc.removeEventListener('mousemove', onMove); doc.removeEventListener('mouseleave', onLeave); }
        doc = next;
        if (doc) { doc.addEventListener('mousemove', onMove); doc.addEventListener('mouseleave', onLeave); }
      } catch {
        doc = null; // cross-origin: no pointer
      }
    };
    attach();
    const timer = setInterval(attach, POLL_MS);
    return () => {
      clearInterval(timer);
      try {
        if (doc) { doc.removeEventListener('mousemove', onMove); doc.removeEventListener('mouseleave', onLeave); }
      } catch { /* gone */ }
      setPresencePointer(projectId, null);
    };
  }, [projectId, base, iframeRef]);

  const openRef = useRef(onOpen);
  openRef.current = onOpen;
  useEffect(() => {
    if (!projectId) { return undefined; }
    const handler = (event) => {
      const detail = event.detail || {};
      if (detail.projectId && detail.projectId !== projectId) { return; }
      if (openRef.current) { openRef.current({ service: detail.service || null, route: detail.route || '/' }); }
    };
    window.addEventListener('collab:openPreview', handler);
    return () => window.removeEventListener('collab:openPreview', handler);
  }, [projectId]);

  return route;
}
